/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/PullRequestDetailsHeader" />
import * as React from "react";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { themeIndependentStyle } from "Repos/Common/Constants";
import { GitVersionDescriptor, PullRequestStatus } from "Repos/Common/Contracts/Git";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IOptionsViewService } from "Repos/Views/Settings/Options/Services/OptionsViewService";
import { IReadonlyObservableArray, ObservableCollection } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { Button, IButtonProps } from "VSSUI/Button";
import { ClipboardButton } from "VSSUI/Clipboard";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { CustomHeader } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { IMenuItem, MenuItemType, MoreButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { IPersonaProvider, Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { SplitButton } from "VSSUI/SplitButton";
import { Status, StatusSize } from "VSSUI/Status";
import { TextField } from "VSSUI/TextField";
import { css, KeyCode } from "VSSUI/Util";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { IAutoCompletingData, ICompletionStatus, IReviewer, ReviewerVote } from "../../Contracts/PullRequest";
import { getPullRequestDiscussionService } from "../../Discussion/PullRequestDiscussionThreadService";
import { hasPermission, PullRequestPermissions } from "../../PullRequestPermissionService";
import { getPullRequestPolicyService } from "../../PullRequestPolicyService";
import { getPullRequestViewService } from "../../PullRequestViewService";
import { getBlockingRequiredReviewers } from "../../Util/Reviewer";
import { getVoteStatusProps } from "../../Util/Vote";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

export class PullRequestDetailsHeader extends VssComponent {
    public render() {
        const prDetailsService = getPullRequestViewService(this.context.pageContext);
        const policyService = getPullRequestPolicyService(this.context.pageContext);

        return (
            <Observer
                completionStatus={prDetailsService.getCompletionStatus()}
                hasBlockingPolicies={policyService.getHasBlockingPolicies()}
                hasDirtyChanges={prDetailsService.getLocalChanges().isDirty()}
                hasUnsatisfiedPolicies={policyService.getHasUnsatisfiedPolicies()}
                isDraft={prDetailsService.getIsDraft()}
                isFollowed={prDetailsService.getIsFollowed()}
                isLiveUpdatesEnabled={prDetailsService.getLiveUpdatesEnabled()}
                isNeedLastIterationApprove={policyService.getIsNeedLastIterationApprove()}
                policiesReady={policyService.getPolicies().ready}
                reviewers={prDetailsService.getReviewers()}
                status={prDetailsService.getStatus()}
                targetBranch={prDetailsService.getTargetBranch()}
            >
                {(props: {
                    completionStatus: ICompletionStatus;
                    hasBlockingPolicies: boolean;
                    hasDirtyChanges: boolean;
                    hasUnsatisfiedPolicies: boolean;
                    isDraft: boolean;
                    isFollowed: boolean;
                    isLiveUpdatesEnabled: boolean;
                    isNeedLastIterationApprove: boolean;
                    policiesReady: boolean;
                    reviewers: IReviewer[];
                    status: PullRequestStatus;
                    targetBranch: string;
                }) => {
                    return <PRHeader {...props} pageContext={this.context.pageContext} />;
                }}
            </Observer>
        );
    }
}

function getVoteMenuItem(pageContext: IVssPageContext, vote: ReviewerVote, actionText: string, isToReapprove = false): IMenuItem {
    return {
        iconProps: {
            render: className => <Status {...getVoteStatusProps(vote)} className={className} size={StatusSize.m} />
        },
        id: "vote-" + actionText,
        onActivate: () => {
            const prDetailsService = getPullRequestViewService(pageContext);
            prDetailsService.setVote(vote, false, isToReapprove);
        },
        text: actionText
    };
}

function getContextMenuItems(props: IPRHeaderProps): IReadonlyObservableArray<IMenuItem> {
    const { isFollowed, isLiveUpdatesEnabled, pageContext, status } = props;

    const prDetailsService = getPullRequestViewService(pageContext);
    const prDiscussionService = getPullRequestDiscussionService(pageContext);

    const canShare = hasPermission(pageContext, PullRequestPermissions.Share);
    const canAddComments = hasPermission(pageContext, PullRequestPermissions.AddEditComment);
    const canDisableLiveUpdate = hasPermission(pageContext, PullRequestPermissions.LiveUpdate);
    const canFollow = hasPermission(pageContext, PullRequestPermissions.Follow);

    const staticMenuItems: IMenuItem[] = [];

    if (canShare) {
        staticMenuItems.push({
            groupKey: "main",
            iconProps: { iconName: "Mail" },
            id: "pr-share",
            onActivate: afterMenuHidesUp(() => prDetailsService.sharePullRequest()),
            text: Resources.SharePullRequest
        });
    }

    if (canAddComments) {
        staticMenuItems.push({
            groupKey: "main",
            iconProps: { iconName: "SaveAll" },
            id: "pr-save-comments",
            onActivate: () => {
                prDiscussionService.saveThreads();
            },
            text: Resources.SaveAllComments
        });
    }

    if (canDisableLiveUpdate) {
        staticMenuItems.push({
            groupKey: "main",
            iconProps: { iconName: isLiveUpdatesEnabled ? "StreamingOff" : "Streaming" },
            id: "pr-disable-updates",
            onActivate: () => prDetailsService.setLiveUpdatesEnabled(!isLiveUpdatesEnabled),
            text: isLiveUpdatesEnabled ? Resources.DisableLiveUpdates : Resources.EnableLiveUpdates
        });
    }

    if (canFollow) {
        staticMenuItems.push({
            groupKey: "notifications",
            iconProps: { iconName: "View" },
            id: "pr-follow",
            onActivate: () => prDetailsService.toggleIsFollowed(),
            text: isFollowed ? Resources.Following : Resources.Follow
        });
    }

    if (status === PullRequestStatus.Active) {
        const routingService = getReposRoutingService(pageContext);
        const targetRepository = prDetailsService.getTargetRepository();

        const lastMergeCommit = prDetailsService.getLastMergeCommit();
        const mergeTargetCommit = prDetailsService.getMergeTargetCommit();
        const mergeChangesUrl =
            lastMergeCommit &&
            mergeTargetCommit &&
            routingService.getBranchCompareUrl(targetRepository.name, targetRepository.project.name, mergeTargetCommit, lastMergeCommit);

        const canMerge = hasPermission(pageContext, PullRequestPermissions.RestartMerge);
        const canRetarget = hasPermission(pageContext, PullRequestPermissions.Retarget);

        staticMenuItems.push({
            disabled: !mergeChangesUrl,
            groupKey: "commits",
            href: mergeChangesUrl,
            iconProps: { iconName: "BranchCommit" },
            id: "pr-view-merge",
            onActivate: (m, e) => onClickFPS(pageContext, mergeChangesUrl!, true, e),
            text: Resources.ViewMergeChanges
        });

        if (canMerge) {
            staticMenuItems.push({
                groupKey: "commits",
                iconProps: { iconName: "BranchMerge" },
                id: "pr-restart-merge",
                onActivate: () => prDetailsService.restartMerge(),
                text: Resources.RestartMerge
            });
        }

        if (canRetarget) {
            staticMenuItems.push({
                groupKey: "commits",
                iconProps: { iconName: "OpenSource" },
                id: "pr-change-target-branch",
                onActivate: afterMenuHidesUp(() => prDetailsService.changeTargetBranch()),
                text: Resources.ChangeTargetBranch
            });
        }
    }

    const canCherryPickRevert = hasPermission(pageContext, PullRequestPermissions.CherryPickRevert);

    if (canCherryPickRevert) {
        staticMenuItems.push({
            groupKey: "commits",
            iconProps: { iconName: "BranchShelveset" },
            id: "pr-cherry-pick",
            onActivate: afterMenuHidesUp(() => prDetailsService.cherryPickPullRequest()),
            text: Resources.CherryPick
        });

        if (status === PullRequestStatus.Completed) {
            staticMenuItems.push({
                groupKey: "commits",
                iconProps: { iconName: "Undo" },
                id: "pr-revert",
                onActivate: afterMenuHidesUp(() => prDetailsService.revertPullRequest()),
                text: Resources.Revert
            });
        }
    }

    const prMenuItems = new ObservableCollection<IMenuItem>();
    prMenuItems.push(staticMenuItems);
    addMenuItems(pageContext, prMenuItems, "ms.vss-code-web.pull-request-action-menu", prDetailsService.getExtensionContext());

    return prMenuItems;
}

interface IPRHeaderProps {
    completionStatus: ICompletionStatus;
    hasBlockingPolicies: boolean;
    hasDirtyChanges: boolean;
    hasUnsatisfiedPolicies: boolean;
    isDraft: boolean;
    isFollowed: boolean;
    isLiveUpdatesEnabled: boolean;
    isNeedLastIterationApprove: boolean;
    pageContext: IVssPageContext;
    policiesReady: boolean;
    reviewers: IReviewer[];
    status: PullRequestStatus;
    targetBranch: string;
}

const maxPersonas = 4; // 4 to keep it consistent with PR list view

const PRHeader = (props: IPRHeaderProps): JSX.Element => {
    const { pageContext, status } = props;

    const prDetailsService = getPullRequestViewService(pageContext);

    const targetRepository = prDetailsService.getTargetRepository();
    const pullRequestId = prDetailsService.getId();

    const optionsViewService = pageContext.getService<IOptionsViewService>("OptionsViewService");
    const [strictVoteMode, setStrictVoteMode] = React.useState(true);

    React.useEffect(() => {
        if (userHasClaim(pageContext, UserClaims.Member)) {
            optionsViewService.getRepositoryOptions(targetRepository.project.id, targetRepository.id, false).then(options => {
                const strictVoteModeKey = "StrictVoteMode";
                const strictVoteOption = options.find(option => option.key === strictVoteModeKey)?.value ?? false;
                setStrictVoteMode(strictVoteOption);
            });
        }
    }, []);

    const reposRoutingService = getReposRoutingService(pageContext);
    const pullRequestUrl = reposRoutingService.getPullRequestUrl(targetRepository.name, targetRepository.project.name, pullRequestId);

    const sourceBranchHref = reposRoutingService.getFilesUrl(
        prDetailsService.getSourceRepository().name,
        prDetailsService.getSourceRepository().project.name,
        undefined,
        branchToVersionDescriptor(prDetailsService.getSourceBranch())
    );

    const canUpdateTitle = hasPermission(pageContext, PullRequestPermissions.UpdateTitleDescription);
    const condensedLayoutConditional = (screenSize: ScreenSize) => screenSize < ScreenSize.small;
    const creatorId = prDetailsService.getCreator().id;
    const expandedLayoutConditional = (screenSize: ScreenSize) => !condensedLayoutConditional(screenSize);
    const hasBlockingRequiredReviewers = getBlockingRequiredReviewers(props.reviewers).length > 0;
    const prMenuItems = getContextMenuItems(props);
    const userId = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
    const userReviewer: IReviewer | undefined = props.reviewers.filter(r => r.identity.id === userId)[0];
    const { isSourceDeleted } = prDetailsService.getCompletionStatus().value;

    const prDiscussionService = getPullRequestDiscussionService(pageContext);
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

    const voteCompleteButtons = (
        <>
            <VoteButton
                {...props}
                canDecline={prDetailsService.canDeclineReviewer(userId)}
                hasDeclined={userReviewer && userReviewer.baseReviewer.hasDeclined}
                primary={userId !== creatorId}
                strictVoteMode={strictVoteMode}
            />
            <CompletionButton
                {...props}
                autoCompletingData={props.completionStatus.autoCompletingData}
                externallyMerged={props.completionStatus.externallyMerged}
                hasBlockingRequiredReviewers={hasBlockingRequiredReviewers}
                hasConflicts={props.completionStatus.hasConflicts}
                hasDirtyChanges={props.hasDirtyChanges}
                isSourceDeleted={isSourceDeleted}
                primary={userId === creatorId}
            />
        </>
    );

    return (
        <Observer fullScreen={layoutManager.getFullScreenMode()}>
            {(fullScreenProps: { fullScreen: boolean }) => (
                <CustomHeader className={css("repos-pr-header margin-bottom-8", fullScreenProps.fullScreen && "full-screen")}>
                    <Observer title={prDetailsService.getTitle()}>
                        {(titleProps: { title: string }) => {
                            // add heading visible only to screen readers to match old experience
                            return (
                                <span role="heading" aria-level={1}>
                                    <span className="screen-reader-only">
                                        {format(
                                            Resources.PullRequestAriaHeading,
                                            prDetailsService.getId(),
                                            getStatusText(props.isDraft, props.status),
                                            titleProps.title
                                        )}
                                    </span>
                                </span>
                            );
                        }}
                    </Observer>

                    {fullScreenProps.fullScreen ? (
                        <div className="flex-row flex-center rhythm-horizontal-8">
                            <PullRequestStatusPill isDraft={props.isDraft} status={props.status} />
                            <span>!{prDetailsService.getId()}</span>
                            <Observer title={prDetailsService.getTitle()}>
                                {(titleProps: { title: string }) => <span className="text-ellipsis">{titleProps.title}</span>}
                            </Observer>
                        </div>
                    ) : (
                        <div className="flex-column flex-grow">
                            <div className="flex-row rhythm-horizontal-8 flex-center flex-grow">
                                <Observer isTitleDirty={prDetailsService.getIsTitleDirty()} title={prDetailsService.getTitle()}>
                                    {(props: { isTitleDirty: boolean; title: string }) => {
                                        return (
                                            <div className="repos-pr-title-row inline-flex-row flex-grow rhythm-horizontal-8 flex-center">
                                                <FocusWithin>
                                                    {(focusStatus: IFocusWithinStatus) => (
                                                        <>
                                                            <TextField
                                                                ariaLabel={Resources.PullRequestTitle}
                                                                className={css(
                                                                    "repos-pr-title title-m flex-grow",
                                                                    status === PullRequestStatus.Active && "editable"
                                                                )}
                                                                containerClassName="flex-grow"
                                                                onBlur={focusStatus.onBlur}
                                                                onChange={(e, title) => canUpdateTitle && prDetailsService.setTitle(title)}
                                                                onFocus={focusStatus.onFocus}
                                                                onKeyDown={e => e.keyCode === KeyCode.enter && prDetailsService.saveTitle()}
                                                                readOnly={status !== PullRequestStatus.Active}
                                                                value={props.title}
                                                            />
                                                            <ClipboardButton
                                                                ariaLabel={Resources.ClipboardTooltip}
                                                                className={css("repos-pr-title-clipboard-button", focusStatus.hasFocus && "visible")}
                                                                getContent={() => (
                                                                    <>
                                                                        <a href={pullRequestUrl} style={themeIndependentStyle}>
                                                                            {format(Resources.PullRequestLinkFormat, pullRequestId)}
                                                                        </a>
                                                                        <span style={{ ...themeIndependentStyle, whiteSpace: "pre" }}>
                                                                            {": "}
                                                                            {props.title}
                                                                        </span>
                                                                    </>
                                                                )}
                                                                onBlur={focusStatus.onBlur}
                                                                onFocus={focusStatus.onFocus}
                                                                showCopiedTooltip={true}
                                                                subtle={true}
                                                                tooltipProps={{ text: Resources.ClipboardTooltip }}
                                                            />
                                                        </>
                                                    )}
                                                </FocusWithin>

                                                {props.isTitleDirty && (
                                                    <Button
                                                        ariaLabel={Resources.SaveTitle}
                                                        iconProps={{ iconName: "Save" }}
                                                        onClick={() => prDetailsService.saveTitle()}
                                                        subtle={true}
                                                        tooltipProps={{ text: Resources.SaveTitle }}
                                                    />
                                                )}
                                                {props.isTitleDirty && (
                                                    <Button
                                                        ariaLabel={Resources.RevertTitle}
                                                        iconProps={{ iconName: "Undo" }}
                                                        onClick={() => prDetailsService.revertTitle()}
                                                        subtle={true}
                                                        tooltipProps={{ text: Resources.RevertTitle }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    }}
                                </Observer>
                                <ScreenSizeConditional condition={expandedLayoutConditional}>{voteCompleteButtons}</ScreenSizeConditional>
                                {prMenuItems.length > 0 && (
                                    <MoreButton
                                        className="bolt-header-command-item-button"
                                        contextualMenuProps={{
                                            menuProps: {
                                                id: "pr-header-submenu",
                                                items: prMenuItems
                                            }
                                        }}
                                        subtle={false}
                                    />
                                )}
                            </div>
                            <div className="flex-row">
                                <div className="pr-secondary-title-row secondary-text flex-row margin-left-8 rhythm-horizontal-4 flex-center flex-wrap">
                                    <div className="pr-secondary-title-row-persona flex-row rhythm-horizontal-8 flex-center flex-wrap">
                                        <PullRequestStatusPill isDraft={props.isDraft} status={props.status} />
                                        <span>!{prDetailsService.getId()}</span>
                                        <Persona
                                            identity={prDetailsService.getCreator()}
                                            personaProvider={prDetailsService.getPersonaProvider()}
                                            size={PersonaSize.size24}
                                        />
                                        <div>{prDetailsService.getCreator().displayName}</div>
                                    </div>
                                    <FormatComponent elementType={"div"} className="pr-header-branches" format={Resources.TitleMergeFormat}>
                                        {prDetailsService.getSourceRepository().id === prDetailsService.getTargetRepository().id ? (
                                            <>
                                                <FocusWithin>
                                                    {(focusStatus: IFocusWithinStatus) => (
                                                        <>
                                                            <FPSLink onBlur={focusStatus.onBlur} onFocus={focusStatus.onFocus} href={sourceBranchHref}>{prDetailsService.getSourceBranch()}</FPSLink>
                                                            <ClipboardButton
                                                                ariaLabel={Resources.ClipboardTooltip}
                                                                className={css("repos-pr-source-branch-clipboard-button", focusStatus.hasFocus && "visible")}
                                                                getContent={() => (
                                                                    <>
                                                                        {prDetailsService.getSourceBranch()}
                                                                    </>
                                                                )}
                                                                onBlur={focusStatus.onBlur}
                                                                onFocus={focusStatus.onFocus}
                                                                showCopiedTooltip={true}
                                                                subtle={true}
                                                                tooltipProps={{ text: Resources.ClipboardTooltip }}
                                                            />
                                                        </>
                                                    )}
                                                </FocusWithin>
                                            </>
                                        ) : (
                                            <>
                                                <FocusWithin>
                                                    {(focusStatus: IFocusWithinStatus) => (
                                                        <>
                                                            <FPSLink onBlur={focusStatus.onBlur} onFocus={focusStatus.onFocus} href={sourceBranchHref}>
                                                                {prDetailsService.getSourceRepository().name} {prDetailsService.getSourceBranch()}
                                                            </FPSLink>
                                                            <ClipboardButton
                                                                ariaLabel={Resources.ClipboardTooltip}
                                                                className={css("repos-pr-source-branch-clipboard-button", focusStatus.hasFocus && "visible")}
                                                                getContent={() => (
                                                                    <>
                                                                        {prDetailsService.getSourceRepository().name} {prDetailsService.getSourceBranch()}
                                                                    </>
                                                                )}
                                                                showCopiedTooltip={true}
                                                                subtle={true}
                                                                tooltipProps={{ text: Resources.ClipboardTooltip }}
                                                            />
                                                        </>
                                                    )}
                                                </FocusWithin>
                                            </>
                                        )}
                                        <FPSLink
                                            href={reposRoutingService.getFilesUrl(
                                                prDetailsService.getTargetRepository().name,
                                                prDetailsService.getTargetRepository().project.name,
                                                undefined,
                                                branchToVersionDescriptor(props.targetBranch)
                                            )}
                                        >
                                            {props.targetBranch}
                                        </FPSLink>
                                    </FormatComponent>
                                    <Observer
                                        resolvedCount={prDiscussionService.getResolvedThreadCount()}
                                        totalCount={prDiscussionService.getTotalThreadCount()}
                                    >
                                        {(commentProps: { resolvedCount: number; totalCount: number }) => {
                                            return commentProps.totalCount > 0 ? (
                                                <div className="pr-header-resolved-comments" role="heading" aria-level={2}>
                                                    {commentProps.resolvedCount < commentProps.totalCount &&
                                                        format(Resources.ResolvedCommentsFormat, commentProps.resolvedCount, commentProps.totalCount)}
                                                    {commentProps.resolvedCount === commentProps.totalCount && Resources.ResolvedAllComments}
                                                </div>
                                            ) : null;
                                        }}
                                    </Observer>
                                    <Observer viewedCount={prDetailsService.getViewedCount()} changes={prDetailsService.getLatestChanges()}>
                                        {(commentProps: { viewedCount: number; changes: IChangeCollection<GitVersionDescriptor> }) => {
                                            const { viewedCount } = commentProps;
                                            const { totalChanges } = commentProps.changes;
                                            return viewedCount > 0 ? (
                                                <div className="pr-header-viewed-files">
                                                    {viewedCount < totalChanges && format(Resources.ReviewedFilesFormat, viewedCount, totalChanges)}
                                                    {viewedCount === totalChanges && Resources.ReviewedAllFiles}
                                                </div>
                                            ) : null;
                                        }}
                                    </Observer>
                                </div>
                                <div className="flex-grow"></div>
                                <div className="pr-secondary-title-row secondary-text flex-row margin-left-8 rhythm-horizontal-8 flex-center flex-wrap">
                                    <Observer reviewers={prDetailsService.getReviewers()}>
                                        {(reviewerProps: { reviewers: IReviewer[] }) => {
                                            const reviewers = reviewerProps.reviewers.filter(
                                                r => !r.baseReviewer.isContainer && !r.baseReviewer.hasDeclined
                                            );
                                            if (reviewers.length > 0) {
                                                // Get the reviewers who are individuals (not grouped), picking out
                                                // the reviewers who have voted first, followed by non-voters
                                                let visibleReviewers = [
                                                    ...reviewers.filter(r => r.identity.id === userReviewer?.identity.id), // always show current user first
                                                    ...reviewers.filter(
                                                        r => r.vote !== ReviewerVote.NoVote && r.identity.id !== userReviewer?.identity.id
                                                    ),
                                                    ...reviewers.filter(
                                                        r => r.vote === ReviewerVote.NoVote && r.identity.id !== userReviewer?.identity.id
                                                    )
                                                ];
                                                let overflowCount = 0;

                                                if (visibleReviewers.length > maxPersonas) {
                                                    overflowCount = visibleReviewers.length - maxPersonas + 1; // +1 because of overflow persona taking up one
                                                    visibleReviewers = visibleReviewers.slice(0, maxPersonas - 1);
                                                }

                                                return (
                                                    <div className="pr-header-reviewers flex-self-start">
                                                        <PersonaGroup overflowCount={overflowCount}>
                                                            {visibleReviewers.map(r => (
                                                                <PullRequestReviewer
                                                                    key={r.identity.id}
                                                                    personaProvider={prDetailsService.getPersonaProvider()}
                                                                    reviewer={r}
                                                                    showTooltip={true}
                                                                />
                                                            ))}
                                                        </PersonaGroup>
                                                    </div>
                                                );
                                            }

                                            return null;
                                        }}
                                    </Observer>
                                </div>
                            </div>
                            <ScreenSizeConditional condition={condensedLayoutConditional}>
                                <div className="flex-row rhythm-horizontal-8 padding-top-16 margin-left-8 flex-stretch">{voteCompleteButtons}</div>
                            </ScreenSizeConditional>
                        </div>
                    )}
                </CustomHeader>
            )}
        </Observer>
    );
};

interface IVoteButtonProps {
    canDecline: boolean;
    hasDeclined: boolean;
    isDraft: boolean;
    isNeedLastIterationApprove: boolean;
    pageContext: IVssPageContext;
    primary: boolean;
    status: PullRequestStatus;
    strictVoteMode: boolean;
}

export const VoteButton = (props: IVoteButtonProps): JSX.Element | null => {
    const { canDecline, hasDeclined, isDraft, isNeedLastIterationApprove, pageContext, primary, status } = props;

    const prDetailsService = getPullRequestViewService(pageContext);
    const isToReapprove = isNeedLastIterationApprove && prDetailsService.isApprovedByCurrentUser();

    const canVote = props.strictVoteMode
        ? hasPermission(pageContext, PullRequestPermissions.Complete)
        : hasPermission(pageContext, PullRequestPermissions.Vote);

    if (!canVote || status !== PullRequestStatus.Active) {
        return null;
    }

    if (isDraft) {
        if (canDecline) {
            return (
                <Button
                    onClick={() => getPullRequestViewService(pageContext).updateDeclineToReview(!hasDeclined)}
                    text={hasDeclined ? Resources.RejoinToReview : Resources.DeclineToReview}
                />
            );
        } else {
            return null;
        }
    }

    return (
        <SplitButton
            buttonProps={{
                onClick: () => {
                    prDetailsService.setVote(ReviewerVote.Approved, false, isToReapprove);
                },
                text: isToReapprove ? Resources.Reapprove : Resources.Approve
            }}
            className="repos-pr-header-vote-button"
            menuButtonProps={{
                contextualMenuProps: {
                    menuProps: {
                        id: "vote",
                        items: [
                            getVoteMenuItem(pageContext, ReviewerVote.Approved, isToReapprove ? Resources.Reapprove : Resources.Approve, isToReapprove),
                            getVoteMenuItem(pageContext, ReviewerVote.ApprovedWithSuggestion, isToReapprove ? Resources.ReapproveWithSuggestions : Resources.ApproveWithSuggestions, isToReapprove),
                            getVoteMenuItem(pageContext, ReviewerVote.WaitingForAuthor, Resources.WaitForAuthor),
                            getVoteMenuItem(pageContext, ReviewerVote.Rejected, Resources.Reject),
                            {
                                id: "separator",
                                itemType: MenuItemType.Divider
                            },
                            canDecline
                                ? {
                                      id: "decline",
                                      onActivate: () => getPullRequestViewService(pageContext).updateDeclineToReview(!hasDeclined),
                                      text: hasDeclined ? Resources.RejoinToReview : Resources.DeclineToReview
                                  }
                                : getVoteMenuItem(pageContext, ReviewerVote.NoVote, Resources.ResetFeedback)
                        ]
                    }
                }
            }}
            primary={primary}
        />
    );
};

interface ICompletionButtonProps {
    autoCompletingData: IAutoCompletingData | undefined;
    externallyMerged: boolean;
    hasBlockingPolicies: boolean;
    hasBlockingRequiredReviewers: boolean;
    hasConflicts: boolean;
    hasDirtyChanges: boolean;
    hasUnsatisfiedPolicies: boolean;
    isDraft: boolean;
    isSourceDeleted: boolean;
    pageContext: IVssPageContext;
    policiesReady: boolean;
    primary: boolean;
    status: PullRequestStatus;
}

const CompletionButton = (props: ICompletionButtonProps): JSX.Element | null => {
    const { pageContext, hasConflicts } = props;

    const prDetailsService = getPullRequestViewService(pageContext);

    const canComplete = hasPermission(pageContext, PullRequestPermissions.Complete);
    const canCancelAutoComplete = hasPermission(pageContext, PullRequestPermissions.CancelAutoComplete);
    const canPublishUnpublish = hasPermission(pageContext, PullRequestPermissions.PublishUnpublish);
    const canAbandonReactivate = hasPermission(pageContext, PullRequestPermissions.AbandonReactivate);

    let primaryButton: IButtonProps | undefined;

    const cannotDeleteSourceMessage = prDetailsService.getSourceDeleteBlockReason();

    const completeDisabled = hasConflicts && !props.externallyMerged;
    const autoCompleteDisabled = props.autoCompletingData ? false : hasConflicts;

    const setAbandoned = () => {
        const service = pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService");
        let unsavedItems: string[] = [];
        const document = {
            isDirty: () => {
                let dirty = false;
                unsavedItems = [];
                if (prDetailsService.getIsTitleDirty().value) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedTitleText);
                }
                if (prDetailsService.getDescriptionEditMode().value) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedDescriptionText);
                }

                if (getPullRequestDiscussionService(pageContext).hasUnsavedThreads()) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedCommentsText);
                }

                if (props.hasDirtyChanges) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedCodeChanges);
                }
                return dirty;
            },
            getDirtyMessage: (): string => format(Resources.UnsavedItemsMessage, unsavedItems.join(", ")),
            getDirtyTitle: (): string => Resources.CannotChangePullRequestStatus,
            getDirtyOnStayBtn: (): string => Resources.Cancel,
            getDirtyOnLeaveBtn: (): string => Resources.Save
        };
        service.registerFirst(document);
        service.triggerNavigateAway(async () => {
            if (prDetailsService.getIsTitleDirty()) {
                await prDetailsService.saveTitle();
            }
            if (prDetailsService.getDescriptionEditMode().value) {
                await prDetailsService.saveDescription();
            }

            if (getPullRequestDiscussionService(pageContext).hasUnsavedThreads()) {
                await getPullRequestDiscussionService(pageContext).saveThreads();
            }

            if (props.hasDirtyChanges) {
                await prDetailsService.commitLocalChanges();
            }
            prDetailsService.setStatus(PullRequestStatus.Abandoned);
        });
        service.unregister(document);
    };

    const menuItems: IMenuItem[] = [];
    if (props.status === PullRequestStatus.Active) {
        if (props.hasDirtyChanges) {
            primaryButton = {
                onClick: () => prDetailsService.commitLocalChanges(),
                iconProps: { iconName: "SaveAll" },
                text: Resources.CommitUnsavedChanges
            };
            menuItems.push({
                iconProps: { iconName: "Undo" },
                id: "undo-changes",
                onActivate: () => prDetailsService.revertLocalChanges(),
                text: Resources.RevertUnsavedChanges
            });
        } else {
            if (props.isDraft) {
                if (canPublishUnpublish) {
                    primaryButton = { iconProps: { iconName: "Send" }, onClick: () => prDetailsService.setDraftMode(false), text: Resources.Publish };
                    menuItems.push({
                        iconProps: { iconName: "Send" },
                        id: "publish",
                        onActivate: () => prDetailsService.setDraftMode(false),
                        text: Resources.Publish
                    });
                }

                if (canAbandonReactivate) {
                    if (!primaryButton) {
                        primaryButton = {
                            iconProps: { iconName: "Delete" },
                            onClick: () => {
                                setAbandoned();
                            },
                            text: Resources.Abandon
                        };
                    } else {
                        menuItems.push({
                            iconProps: { iconName: "Delete" },
                            id: "abandon",
                            onActivate: () => {
                                setAbandoned();
                            },
                            text: Resources.Abandon
                        });
                    }
                }
            } else {
                const autoCompleteText = props.autoCompletingData ? Resources.CancelAutoComplete : Resources.SetAutoComplete;
                const autoCompleteAction = () => {
                    if (props.autoCompletingData) {
                        prDetailsService.cancelAutoComplete();
                    } else {
                        prDetailsService.completePullRequest(true);
                    }
                };

                const completeText = props.externallyMerged ? Resources.Close : Resources.Complete;
                const completeIconProps = props.externallyMerged ? undefined : { iconName: "BranchMerge" };
                const completeAction = () => {
                    if (props.externallyMerged) {
                        prDetailsService.closePullRequest();
                    } else {
                        prDetailsService.completePullRequest(false);
                    }
                };

                if (canComplete && canCancelAutoComplete) {
                    primaryButton =
                        props.hasBlockingPolicies || props.hasBlockingRequiredReviewers || props.autoCompletingData
                            ? {
                                  iconProps: { iconName: "TriggerAuto" },
                                  onClick: autoCompleteAction,
                                  text: autoCompleteText,
                                  disabled: autoCompleteDisabled
                              }
                            : {
                                  iconProps: completeIconProps,
                                  onClick: completeAction,
                                  text: completeText,
                                  disabled: completeDisabled
                              };

                    menuItems.push({
                        disabled: completeDisabled,
                        iconProps: completeIconProps,
                        id: "complete",
                        onActivate: completeAction,
                        text: completeText
                    });
                }

                if (canComplete && (props.hasUnsatisfiedPolicies || props.hasBlockingRequiredReviewers)) {
                    menuItems.push({
                        disabled: autoCompleteDisabled,
                        iconProps: { iconName: "TriggerAuto" },
                        id: "autoComplete",
                        onActivate: () => autoCompleteAction(),
                        text: autoCompleteText
                    });
                }

                if (canPublishUnpublish) {
                    if (!primaryButton) {
                        primaryButton = {
                            iconProps: { iconName: "EditNote" },
                            onClick: () => prDetailsService.setDraftMode(true),
                            text: Resources.MarkAsDraft
                        };
                    } else {
                        menuItems.push({
                            iconProps: { iconName: "EditNote" },
                            id: "markAsDraft",
                            onActivate: () => prDetailsService.setDraftMode(true),
                            text: Resources.MarkAsDraft
                        });
                    }
                }

                if (canAbandonReactivate) {
                    if (!primaryButton) {
                        primaryButton = {
                            iconProps: { iconName: "Delete" },
                            onClick: () => {
                                setAbandoned();
                            },
                            text: Resources.Abandon
                        };
                    } else {
                        menuItems.push({
                            iconProps: { iconName: "Delete" },
                            id: "abandon",
                            onActivate: () => {
                                setAbandoned();
                            },
                            text: Resources.Abandon
                        });
                    }
                }
            }
        }
    } else if (canAbandonReactivate && props.status === PullRequestStatus.Abandoned) {
        if (!props.isSourceDeleted) {
            primaryButton = {
                onClick: () => prDetailsService.setStatus(PullRequestStatus.Active),
                text: Resources.Reactivate
            };

            menuItems.push({
                iconProps: { iconName: "Delete" },
                id: "delete-source-branch",
                onActivate: () => prDetailsService.deleteSourceBranch(),
                text: Resources.DeleteSourceBranch,
                disabled: !!cannotDeleteSourceMessage
            });
        }
        else {
            primaryButton = {
                text: Resources.Reactivate,
                disabled: true,
                tooltipProps: {text: Resources.CannotReactivatePullRequest}
            };
        }

    } else if (!props.isSourceDeleted && props.status === PullRequestStatus.Completed) {
        primaryButton = {
            onClick: () => prDetailsService.deleteSourceBranch(),
            text: Resources.DeleteSourceBranch,
            disabled: !!cannotDeleteSourceMessage,
            tooltipProps: cannotDeleteSourceMessage ? { text: cannotDeleteSourceMessage } : undefined
        };
    }

    if (menuItems.length > 0 && primaryButton) {
        return (
            <SplitButton
                buttonProps={primaryButton}
                className="repos-pr-header-complete-button"
                disabled={!props.policiesReady}
                menuButtonProps={{
                    contextualMenuProps: {
                        menuProps: {
                            id: "completion",
                            items: menuItems
                        }
                    }
                }}
                primary={props.primary && !completeDisabled}
            />
        );
    } else if (primaryButton) {
        return <Button {...primaryButton} primary={props.primary} />;
    } else {
        return null;
    }
};

interface IPullRequestStatusPillProps {
    isDraft: boolean;
    status: PullRequestStatus;
}

const PullRequestStatusPill = (props: IPullRequestStatusPillProps): JSX.Element | null => {
    const { isDraft, status } = props;

    let text: string | undefined;
    let cssClass: string | undefined;

    switch (status) {
        case PullRequestStatus.Active:
            if (isDraft) {
                text = Resources.Draft;
                cssClass = "pr-status-draft";
            } else {
                text = Resources.StatusActive;
                cssClass = "pr-status-active";
            }
            break;
        case PullRequestStatus.Abandoned:
            text = Resources.StatusAbandoned;
            cssClass = "pr-status-abandoned";
            break;
        case PullRequestStatus.Completed:
            text = Resources.StatusCompleted;
            cssClass = "pr-status-completed";
            break;
    }

    return text ? (
        <Pill className={cssClass} excludeFocusZone={true} excludeTabStop={true}>
            {text}
        </Pill>
    ) : null;
};

function getStatusText(isDraft: boolean, status: PullRequestStatus): string {
    let text = "";
    switch (status) {
        case PullRequestStatus.Active:
            if (isDraft) {
                text = Resources.Draft;
            } else {
                text = Resources.StatusActive;
            }
            break;
        case PullRequestStatus.Abandoned:
            text = Resources.StatusAbandoned;
            break;
        case PullRequestStatus.Completed:
            text = Resources.StatusCompleted;
            break;
    }

    return text;
}

interface IPersonaGroupProps {
    overflowCount: number;
}

const PersonaGroup = (props: IPersonaGroupProps & Readonly<{ children?: React.ReactNode }>) => {
    let overflowPill = null;
    if (props.overflowCount) {
        overflowPill = (
            <Pill size={PillSize.compact} variant={PillVariant.standard}>
                {"+" + props.overflowCount}
            </Pill>
        );
    }
    const spacingClassName = "rhythm-horizontal-8";
    return (
        <div className={css("flex-row flex-center", spacingClassName)}>
            {props.children}
            {overflowPill}
        </div>
    );
};

interface IPullRequestReviewerProps {
    personaProvider?: IPersonaProvider;
    reviewer: IReviewer;
    showTooltip: boolean;
}

const PullRequestReviewer = (props: IPullRequestReviewerProps) => {
    return (
        <div className="pr-reviewer relative">
            <Persona
                identity={props.reviewer.identity}
                personaProvider={props.personaProvider}
                size={PersonaSize.size24}
                tooltipProps={props.showTooltip ? { renderContent: () => <PullRequestReviewerTooltipContent reviewer={props.reviewer} /> } : null}
            />
            <PullRequestReviewerVoteIcon isInTooltip={!props.showTooltip} vote={props.reviewer.vote} />
        </div>
    );
};

interface IPullRequestReviewerTooltipContentProps {
    reviewer: IReviewer;
}

const PullRequestReviewerTooltipContent = (props: IPullRequestReviewerTooltipContentProps) => {
    return (
        <div className="flex-row flex-center">
            <PullRequestReviewer reviewer={props.reviewer} showTooltip={false} />
            <div className="flex-column margin-left-8">
                <span>{props.reviewer.identity.displayName}</span>
                <span>{getVoteText(props.reviewer.vote)}</span>
            </div>
        </div>
    );
};

interface IPullRequestReviewerVoteIconProps {
    isInTooltip: boolean;
    vote: ReviewerVote;
}

function getVoteText(vote: ReviewerVote) {
    switch (vote) {
        case ReviewerVote.Approved:
            return Resources.Approved;
        case ReviewerVote.ApprovedWithSuggestion:
            return Resources.ApprovedWithSuggestions;
        case ReviewerVote.WaitingForAuthor:
            return Resources.WaitingForAuthor;
        case ReviewerVote.Rejected:
            return Resources.Rejected;
        default:
            return "";
    }
}

const PullRequestReviewerVoteIcon = (props: IPullRequestReviewerVoteIconProps) => {
    let iconName,
        voteClassName,
        ariaLabel = "";
    switch (props.vote) {
        case ReviewerVote.Approved:
        case ReviewerVote.ApprovedWithSuggestion:
            iconName = "CompletedSolid";
            voteClassName = "approved";
            ariaLabel = "";
            break;
        case ReviewerVote.WaitingForAuthor:
            iconName = "AwayStatus";
            voteClassName = "waiting";
            ariaLabel = "";
            break;
        case ReviewerVote.Rejected:
            iconName = "StatusErrorFull";
            voteClassName = "rejected";
            ariaLabel = "";
    }
    if (iconName) {
        return (
            <Icon
                iconName={iconName}
                className={css("repos-pr-reviewer-vote absolute", voteClassName, props.isInTooltip && "tooltip")}
                ariaLabel={ariaLabel}
            />
        );
    }
    return null;
};

/**
 * Executes this action asynchronously to give time to current callout to be closed.
 * Reason being that otherwise the displaying panel would get the wrong element to return focus to.
 */
function afterMenuHidesUp(action: () => void) {
    return () => {
        setTimeout(action, 100);
    };
}
